import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { Brand } from '../components/Brand'
import { getTags, getSlogans, startRound, submitScore } from '../api/gg'
import { useMediaFeed } from '../hooks/useMediaFeed'
import { buildFeedTags, formatSexualitySummary } from '../lib/feedPrefs'
import { GalleryPreview } from '../components/GalleryPreview'
import { TagPicker } from '../components/TagPicker'
import { MediaTile } from '../components/MediaTile'
import { SplashLayer, type SplashHandle } from '../components/SplashLayer'
import { useScreenShake } from '../lib/useScreenShake'
import { initRound, registerHit, projectedFinal, type RoundState } from '../lib/scoring'
import { pickSlogan } from '../lib/splash'
import { useAuth } from '../store/auth'
import { useGame } from '../store/game'
import type { ContentKind, Slogan, Tag } from '../types'

type Phase = 'pick' | 'loading' | 'play' | 'done'

const ROUND_MS = 90_000 // hard cap; the player can hit the BUZZER any time

interface Summary {
  rawScore: number
  finalScore: number
  verifiedRaw?: number
  multiplier: number
  hits: number
  bestCombo: number
  durationMs: number
  speedBonus: number
  banked: boolean
  bankError?: string
}

export function GameMode() {
  const nav = useNavigate()
  const token = useAuth((s) => s.token)
  const user = useAuth((s) => s.user)
  const setSelection = useGame((s) => s.setSelection)
  const setMultiplier = useGame((s) => s.setMultiplier)
  const feedPrefs = useGame((s) => s.feedPrefs)

  const [phase, setPhase] = useState<Phase>('pick')
  const [tags, setTags] = useState<Tag[]>([])
  const [slogans, setSlogans] = useState<Slogan[]>([])
  const { items: queue, progress, loading, error: feedError, startGame, cancel, setError: setFeedError } =
    useMediaFeed()
  const [index, setIndex] = useState(0)
  const [round, setRound] = useState<RoundState>(initRound)
  const [remainingMs, setRemainingMs] = useState(ROUND_MS)
  const [multiplier, setMult] = useState(1)
  const [summary, setSummary] = useState<Summary | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [tagLabels, setTagLabels] = useState<string[]>([])

  const roundRef = useRef<RoundState>(initRound())
  const roundIdRef = useRef<string | null>(null)
  const startRef = useRef(0)
  const playedTagsRef = useRef<string[]>([])
  const splashRef = useRef<SplashHandle>(null)
  const { shaking, key: shakeKey, shake } = useScreenShake()

  // Load tags + slogans once.
  useEffect(() => {
    void getTags().then((t) =>
      setTags(t.map((x) => ({ ...x, enabled: true, sort: 0 }))),
    )
    void getSlogans().then(setSlogans)
    return () => cancel()
  }, [cancel])

  const start = useCallback(
    async (tagIds: string[], kind: ContentKind) => {
      const chosen = tags.filter((t) => tagIds.includes(t.id))
      if (!chosen.length) return
      setSelection(tagIds, kind)
      setTagLabels(chosen.map((t) => t.label))
      playedTagsRef.current = chosen.map((t) => t.label)
      setLoadError(null)
      setFeedError(null)
      setPhase('loading')

      try {
        const items = await startGame(buildFeedTags(chosen, feedPrefs), kind)
        if (!items?.length) {
          setPhase('pick')
          return
        }
        await beginPlay()
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : 'load failed')
        setPhase('pick')
      }
    },
    [tags, setSelection, feedPrefs, startGame, setFeedError],
  )

  async function beginPlay() {
    roundRef.current = initRound()
    setRound(roundRef.current)
    setIndex(0)
    setRemainingMs(ROUND_MS)
    startRef.current = performance.now()
    roundIdRef.current = null

    if (token) {
      try {
        const r = await startRound(token)
        roundIdRef.current = r.roundId
        setMult(r.multiplier)
        setMultiplier(r.multiplier)
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : 'Could not start round')
        setPhase('pick')
        return
      }
    } else {
      setMult(1)
      setMultiplier(1)
    }
    setPhase('play')
  }

  const end = useCallback(async () => {
    setPhase((p) => {
      if (p !== 'play') return p
      return 'done'
    })
    const st = roundRef.current
    const elapsedSec = Math.max(1, (performance.now() - startRef.current) / 1000)
    // "Time vs content": reward fast tapping with a speed bonus.
    const speedBonus = Math.min(st.hits * 200, Math.round((st.hits / elapsedSec) * 400))
    const rawScore = st.score + speedBonus
    const result = {
      score: rawScore,
      hits: st.hits,
      durationMs: Math.round(elapsedSec * 1000),
      bestCombo: st.bestCombo,
      tags: playedTagsRef.current,
    }
    let finalScore = projectedFinal(rawScore, multiplier)
    let verifiedRaw: number | undefined
    let banked = false
    let bankError: string | undefined
    let mult = multiplier
    if (token && roundIdRef.current) {
      try {
        const res = await submitScore(token, { ...result, roundId: roundIdRef.current })
        finalScore = res.finalScore
        verifiedRaw = res.verifiedRaw
        mult = res.multiplier
        banked = true
      } catch (e) {
        bankError = e instanceof Error ? e.message : 'Score not banked'
      }
    } else if (token) {
      bankError = 'Round token missing — score not banked'
    }
    roundIdRef.current = null
    setSummary({
      rawScore,
      finalScore,
      verifiedRaw,
      multiplier: mult,
      hits: st.hits,
      bestCombo: st.bestCombo,
      durationMs: result.durationMs,
      speedBonus,
      banked,
      bankError,
    })
  }, [token, multiplier])

  // Round timer.
  useEffect(() => {
    if (phase !== 'play') return
    const iv = window.setInterval(() => {
      const left = ROUND_MS - (performance.now() - startRef.current)
      if (left <= 0) {
        setRemainingMs(0)
        void end()
      } else {
        setRemainingMs(left)
      }
    }, 100)
    return () => window.clearInterval(iv)
  }, [phase, end])

  const onTap = useCallback(
    (e: React.PointerEvent) => {
      if (phase !== 'play') return
      const now = performance.now()
      const outcome = registerHit(roundRef.current, now)
      roundRef.current = outcome.next
      setRound(outcome.next)
      const x = (e.clientX / window.innerWidth) * 100
      const y = (e.clientY / window.innerHeight) * 100
      splashRef.current?.burst(pickSlogan(slogans, outcome.kind), outcome.kind, { x, y })
      shake()
      setIndex((i) => (queue.length ? (i + 1) % queue.length : 0))
    },
    [phase, slogans, queue.length, shake],
  )

  const cancelLoad = () => {
    cancel()
    setPhase('pick')
  }

  const current = queue[index]
  const timePct = Math.max(0, Math.min(1, remainingMs / ROUND_MS))
  const elapsedSec = (ROUND_MS - remainingMs) / 1000
  const elapsedMin = Math.floor(elapsedSec / 60).toString().padStart(2, '0')
  const elapsedRem = Math.floor(elapsedSec % 60).toString().padStart(2, '0')
  const intensityPct = Math.min(100, Math.round((round.combo + round.hits / 3) * 6.5))
  const intensitySegs = 20
  const mosaicItems = queue.length
    ? Array.from({ length: 6 }, (_, i) => queue[(index + i) % queue.length])
    : []

  // ── render ──
  if (phase === 'pick')
    return (
      <main className="px-4 py-8">
        {loadError || feedError ? (
          <div className="mx-auto mb-4 max-w-2xl rounded-lg border-2 border-neon-red bg-neon-red/10 px-4 py-2 text-center font-bold text-neon-red">
            {loadError ?? feedError}
          </div>
        ) : null}
        <TagPicker tags={tags} onStart={start} ctaLabel="START GOONING SESSION" />
        {!token ? (
          <p className="mt-6 text-center text-sm text-white/50">
            Guest mode — <Link className="underline" to="/login">sign in</Link> to bank scores &amp; earn multipliers.
          </p>
        ) : null}
      </main>
    )

  if (phase === 'loading' || loading)
    return (
      <GalleryPreview
        modeLabel="GOON MODE"
        tags={tagLabels}
        sexuality={formatSexualitySummary(feedPrefs)}
        fraction={progress.fraction}
        count={progress.count}
        note={progress.note}
        onCancel={cancelLoad}
      />
    )

  if (phase === 'play')
    return (
      <main className="relative select-none bg-[#050508] px-3 py-3 pb-8" style={{ touchAction: 'manipulation' }}>
        <SplashLayer ref={splashRef} />

        {/* TOP 3-PANEL HUD — gameplay ref */}
        <div className="mx-auto flex max-w-[980px] gap-2 text-white font-comic shadow-cyber rounded-xl border border-edge/60 p-1 bg-surface/40">
          <div className="hud-segment">
            <div className="hud-label flex items-center justify-center gap-1"><span>⏱</span> ELAPSED</div>
            <div className="hud-value tabular-nums text-white text-3xl">{elapsedMin}:{elapsedRem}</div>
          </div>
          <div className="hud-segment flex-[1.4] border-pow/30">
            <div className="hud-label">CURRENT SCORE</div>
            <div className="score-giant !text-[2.75rem] tabular-nums tracking-[-0.5px]">{round.score.toLocaleString()}</div>
            <div className="text-[10px] uppercase tracking-[2px] text-muted/70 -mt-1">PTS</div>
          </div>
          <div className="hud-segment">
            <div className="hud-label">MULTIPLIER</div>
            <div className="hud-value text-3xl text-pow neon-pink">×{multiplier.toFixed(1)}</div>
          </div>
        </div>

        {/* TIMER BAR */}
        <div className="mx-auto mt-2 h-2.5 max-w-[980px] overflow-hidden rounded-full border border-edge bg-[#111]">
          <div className={clsx('h-full transition-all', timePct < 0.18 ? 'bg-neon-red' : 'bg-kapow')} style={{ width: `${timePct * 100}%` }} />
        </div>

        <div className="mx-auto mt-3 flex max-w-[1020px] gap-3">
          {/* LEFT: CHALLENGE TAGS sidebar — gameplay ref */}
          <div className="hidden w-48 shrink-0 md:block">
            <div className="cyber-panel p-3">
              <div className="mb-2 text-[10px] uppercase tracking-[2px] text-muted font-bold">CHALLENGE TAGS</div>
              <div className="space-y-px">
                {(playedTagsRef.current.length ? playedTagsRef.current : ['EDGING','GOON FUEL','BIG ASS','CUMSHOT','TEASE DENIAL','LOAD BUILD UP','NSFW','TEASE']).slice(0,8).map((lab, idx) => {
                  const active = idx === 0
                  const icons = ['➕','🔥','✨','💧','⏳','📈']
                  return (
                    <div key={idx} className={clsx('tag-cyber text-xs', active && 'active')}>
                      <span>{icons[idx % icons.length]}</span>
                      <span className="flex-1 truncate">{lab}</span>
                    </div>
                  )
                })}
              </div>
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-muted mb-1">PROGRESS</div>
                <div className="h-2 rounded bg-edge overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-boom via-pow to-boom transition-all"
                    style={{ width: `${Math.min(100, timePct * 100)}%` }}
                  />
                </div>
                <div className="mt-0.5 text-right text-[10px] tabular-nums text-muted">
                  {round.hits} HITS · {elapsedMin}:{elapsedRem} / 1:30
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 border-t border-edge pt-3">
                <Brand size="sm" className="!text-lg" />
              </div>
            </div>
          </div>

          {/* CENTRAL TAP ZONE — mosaic grid from gameplay ref */}
          <div className="flex-1">
            <div
              key={shakeKey}
              onPointerDown={onTap}
              className={clsx(
                'relative mx-auto aspect-[16/10] max-h-[58vh] w-full max-w-[780px] cursor-pointer overflow-hidden rounded-2xl border-2 border-edge bg-[#050508] shadow-cyber',
                shaking && 'animate-shake'
              )}
            >
              {/* Blurred mosaic background grid */}
              <div className="absolute inset-0 grid grid-cols-3 grid-rows-2 gap-1 p-1">
                {mosaicItems.map((item, i) => (
                  <div key={i} className={clsx('mosaic-cell', i === 0 && 'active ring-1 ring-pow/50')}>
                    {item?.src ? (
                      <img src={item.src} alt="" draggable={false} />
                    ) : (
                      <div className="h-full w-full bg-surface" />
                    )}
                  </div>
                ))}
              </div>

              {/* Dark overlay for depth */}
              <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(5,5,8,0.55)_100%)]" />

              {/* Active image centered */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-8">
                {current ? (
                  <div className="h-full w-full max-w-[55%] overflow-hidden rounded-xl border-2 border-pow/40 shadow-neon-pink">
                    <MediaTile item={current} eager className="h-full w-full border-0 rounded-none" />
                  </div>
                ) : (
                  <span className="font-comic text-2xl text-white/40">LOADING FEED…</span>
                )}
              </div>

              {/* Floating tag overlays */}
              <div className="pointer-events-none absolute left-3 top-3 flex flex-wrap gap-1.5">
                {(playedTagsRef.current.length ? playedTagsRef.current : ['EDGING', 'GOON FUEL']).slice(0, 2).map((t, i) => (
                  <span key={i} className="rounded bg-black/75 px-2 py-px text-[10px] font-bold tracking-widest text-pow ring-1 ring-pow/70 -rotate-2">{t}</span>
                ))}
              </div>
              <div className="pointer-events-none absolute right-4 top-1/3 rounded bg-black/75 px-2 py-px text-[10px] font-bold tracking-widest text-neon-red ring-1 ring-neon-red/70 rotate-3">CUMSHOT</div>
              <div className="pointer-events-none absolute left-1/4 bottom-1/4 rounded bg-black/75 px-2 py-px text-[10px] font-bold tracking-widest text-kapow ring-1 ring-kapow/70 -rotate-6">BIG ASS</div>

              <div className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(transparent,transparent_3px,rgba(255,255,255,0.025)_3px,rgba(255,255,255,0.025)_4px)]" />
            </div>

            {/* GOON INTENSITY — segmented cyan bar from ref */}
            <div className="mx-auto mt-3 max-w-[780px] flex items-center gap-3 px-1 text-[10px] uppercase tracking-[1.5px] text-muted">
              <span className="shrink-0">GOON INTENSITY</span>
              <div className="intensity-segments">
                {Array.from({ length: intensitySegs }, (_, i) => (
                  <div
                    key={i}
                    className={clsx('intensity-seg', i < Math.round((intensityPct / 100) * intensitySegs) && 'lit')}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* right spacer for balance on large screens */}
          <div className="hidden w-44 shrink-0 lg:block" />
        </div>

        {/* BIG RED HEX BUZZER */}
        <div className="mx-auto mt-5 max-w-[480px] text-center">
          <button onClick={() => void end()} className="buzzer w-full">
            HIT THE BUZZER
            <span className="mt-1 flex items-center justify-center gap-1 text-xs tracking-[3px] text-white/70">
              <span>⚠</span> EMERGENCY EXIT
            </span>
          </button>
          <p className="mt-2 text-xs text-white/40">Tap the feed as fast as you can. Bank it when you peak.</p>
        </div>
      </main>
    )

  // done — results screen from base ref4
  const rankBadge = (summary?.bestCombo ?? 0) >= 12 ? 'FLUFFER!' : (summary?.hits ?? 0) > 18 ? 'GOON PUMP' : (summary?.bestCombo ?? 0) >= 7 ? 'LEAKER' : 'EDGER'
  const durationSec = Math.round((summary?.durationMs ?? 0) / 1000)
  const durationMin = Math.floor(durationSec / 60)
  const durationRem = durationSec % 60
  const avgSpeed = Math.round(((summary?.hits ?? 1) / Math.max(1, durationSec)) * 10) / 10
  const playerName = user?.displayName ?? user?.handle ?? 'Gooner_Guy_69'
  const teaserLeaders = [
    { name: 'PixelPump', pts: 12850, crown: true },
    { name: 'StrokeLord', pts: 9420, crown: true },
    { name: 'Cummander', pts: 7310, crown: true },
    { name: 'ViscousVic', pts: 6660 },
    { name: playerName, pts: summary?.finalScore ?? 4520, you: true },
  ]

  return (
    <main className="relative min-h-[76vh] px-3 py-6">
      {/* User account pill — top right per results ref */}
      {user ? (
        <div className="absolute right-4 top-4 flex items-center gap-2 rounded-full border border-kapow/40 bg-surface/90 px-3 py-1.5 text-sm backdrop-blur shadow-neon-cyan">
          <span className="text-kapow" aria-hidden>🦋</span>
          <span className="font-bold text-white">{playerName}</span>
          <span className="text-pow">✓</span>
        </div>
      ) : null}

      <div className="mx-auto w-full max-w-[980px]">
        <div className="grid gap-6 md:grid-cols-[1fr,300px]">
          {/* Main results column */}
          <div>
            <div className="graffiti-title">SESSION COMPLETE!</div>

            <div className="mt-2 score-giant tabular-nums">
              {(summary?.finalScore ?? 0).toLocaleString()} PTS
            </div>

            <div className="mt-3 inline-flex items-center gap-2 rounded-lg border-2 border-pow bg-pow/10 px-5 py-1 font-comic text-lg tracking-wide text-pow shadow-neon-pink">
              <span>⚠</span> {rankBadge}
            </div>

            {summary?.banked ? (
              <div className="mt-3 font-comic text-xl text-kapow">
                BANKED. GOONED.
                {summary.verifiedRaw !== undefined && summary.verifiedRaw < summary.rawScore ? (
                  <span className="mt-1 block text-xs font-sans text-muted">
                    Server verified {summary.verifiedRaw.toLocaleString()} raw pts
                  </span>
                ) : null}
              </div>
            ) : summary?.bankError ? (
              <div className="mt-3 max-w-md rounded border border-neon-red/50 bg-neon-red/10 px-3 py-2 text-xs text-neon-red">
                {summary.bankError}
              </div>
            ) : !token ? (
              <div className="mt-3 max-w-md rounded border border-edge bg-surface2/80 px-3 py-2 text-xs text-muted">
                Guest run — <Link to="/login" className="text-kapow underline">sign in</Link> to bank scores on the leaderboard.
              </div>
            ) : null}

            {/* Stats row with icons */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className="stat-col">
                <span className="stat-col-icon">⏱</span>
                <span className="text-[10px] uppercase tracking-widest text-muted">TIME</span>
                <span className="font-comic text-xl text-white">
                  {durationMin > 0 ? `${durationMin}m ` : ''}{durationRem}s
                </span>
              </div>
              <div className="stat-col">
                <span className="stat-col-icon">📦</span>
                <span className="text-[10px] uppercase tracking-widest text-muted">ITEMS</span>
                <span className="font-comic text-xl text-white">{summary?.hits ?? 0}</span>
              </div>
              <div className="stat-col">
                <span className="stat-col-icon">⚡</span>
                <span className="text-[10px] uppercase tracking-widest text-muted">AVERAGE SPEED</span>
                <span className="font-comic text-xl text-kapow tabular-nums">{avgSpeed} U/S FAST</span>
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <button
                onClick={() => { setSummary(null); setPhase('pick') }}
                className="btn-goon flex flex-col items-center px-10 py-3 text-lg"
              >
                <span>⚡ TRY AGAIN</span>
                <span className="text-xs tracking-widest opacity-80">×{multiplier.toFixed(1)} MULTIPLIER</span>
              </button>
              <button onClick={() => nav('/scoreboard')} className="rounded-full border border-edge bg-surface2 px-5 py-2.5 text-sm font-comic uppercase tracking-widest text-white/80 hover:text-white hover:border-kapow transition">
                🏆 View Leaderboard
              </button>
              <button
                onClick={() => alert('Replay capture saved locally (future feature)')}
                className="rounded-full border border-edge bg-surface2 px-5 py-2.5 text-sm font-comic uppercase tracking-widest text-white/80 hover:text-kapow transition"
              >
                💾 Save Replay
              </button>
            </div>
          </div>

          {/* Leaderboard teaser sidebar */}
          <div className="leader-teaser self-start">
            <div className="mb-3 flex items-center gap-2 border-b border-edge pb-2">
              <span className="text-lg">🏆</span>
              <div>
                <div className="text-xs uppercase tracking-[2px] text-muted">LEADERBOARD TEASER</div>
                <div className="text-sm font-bold text-white">TOP GOONERS</div>
              </div>
            </div>
            <div className="space-y-1.5 text-sm">
              {teaserLeaders.map((l, idx) => (
                <div
                  key={idx}
                  className={clsx(
                    'flex items-center justify-between rounded-lg px-2 py-1.5',
                    l.you ? 'bg-kapow/15 text-kapow ring-1 ring-kapow/40' : 'text-[#e7e7f2]',
                  )}
                >
                  <span className="flex items-center gap-1.5 truncate">
                    <span className="w-5 text-center text-xs text-muted">{idx + 1}</span>
                    {l.crown ? '👑' : ''} {l.name}
                  </span>
                  <span className="font-comic tabular-nums shrink-0">{l.pts.toLocaleString()} PTS</span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-center text-[10px] text-muted/70">…and 1,337 others</div>
          </div>
        </div>

        <div className="mt-10 flex flex-col items-center gap-1">
          <Brand size="md" />
          <p className="text-[10px] tracking-[3px] text-boom/70">CAPTURE. GOON. CLIMB.</p>
        </div>
      </div>
    </main>
  )
}


